<template>
    <v-container fluid class="pa-0">
        <template v-if="loading">
            <v-overlay :value="loading">
                <v-progress-circular
                    :size="100"
                    color="white"
                    indeterminate
                ></v-progress-circular>
            </v-overlay>
        </template>
        <template v-else>
            <tutor-hero 
                :id="tutor.userData.id"
                :firstName="tutor.userData.firstName"
                :lastName="tutor.userData.lastName"
                :bio="tutor.userData.bio"
                :education="tutor.userData.education"
                :teachingExperience="tutor.userData.teachingExperience"
                :photo="profilePicture"
                :subjects="tutor.teachingSubjects"
                :country="tutor.country"
                :review="tutor.reviewCount"
                :rating="tutor.rating"
                :selectedLanguages="languages"
            />
            <v-container class="pt-0 pb-6 pb-lg-12">
                <v-row justify="center">
                    <v-col lg="6">
                        <template v-if="bookedSuccessfull">
                            <v-alert
                                type="success"
                                prominent
                                class="mb-4 mb-lg-8 mx-auto"
                            >
                                <div class="d-flex align-center justify-space-between">
                                    <span class="font-weight-medium">{{ $t('SuccessfulBookingNotification') }}</span> 
                                    <v-btn outlined rounded :to="`/${$i18n.locale}/student/sessions`">{{ $t('Sessions') }}</v-btn>
                                </div>
                            </v-alert>
                        </template>
                         <template v-if="timeAlreadyBooked">
                            <v-alert
                                type="error"
                                prominent
                                class="mb-4 mb-lg-8 mx-auto"
                            >
                                <div class="d-flex align-center justify-space-between">
                                    <span class="font-weight-medium">{{ $t('TimeAlreadyBooked') }}</span> 
                                    <v-btn outlined rounded :to="`/${$i18n.locale}/student/sessions`">{{ $t('Sessions') }}</v-btn>
                                </div>
                            </v-alert>
                        </template>
                        <template v-else-if="timeslot.isBooked">
                            <v-alert
                                type="info"
                                color="primary"
                                class="mx-auto mb-6"
                            >{{ $t('BookedCalendarSession') }}</v-alert>
                        </template>
                        <h2 class="display-1 mb-4 text-center" v-text="$t('BookRequestTitle')"></h2>
                        <p class="body-1 text-center" v-text="$t('BookRequestDescription')"></p>
                    </v-col>
                </v-row>
                <v-row justify="center">
                    <v-col lg="6">                        
                        <v-card outlined class="custom-radius mb-6 mb-lg-12">
                            <v-card-title v-text="$t('BookingDetails')"></v-card-title>
                            <v-divider></v-divider>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="font-weight-medium">
                                        <v-icon>mdi-calendar-text</v-icon>
                                        {{ $t('SessionDate') }}: {{ dateFormat(timeslot.startTime.localTime) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="font-weight-medium">
                                        <v-icon>mdi-clock-start</v-icon>
                                        {{ $t('SessionStartTime') }}: {{ time12hFormat(timeslot.startTime.localTime) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="font-weight-medium">
                                        <v-icon>mdi-clock-end</v-icon>
                                        {{ $t('SessionEndTime') }}: {{ time12hFormat(timeslot.endTime.localTime) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title class="font-weight-medium">
                                        <v-icon>mdi-currency-usd</v-icon>
                                        {{ $t('Price') }}: {{ price }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-card>                        
                        <template v-if="!bookedSuccessfull && !timeslot.isBooked">
                            <v-row>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-select
                                        rounded
                                        outlined
                                        required
                                        v-model="selectedSubjectId"
                                        :rules="rules.subjectId"
                                        :items="tutor.teachingSubjects"
                                        item-text="label"
                                        item-value="id"
                                        :menu-props="{ offsetY: true }"
                                        :label="$t('Subjects')"
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-select
                                        rounded
                                        outlined
                                        required
                                        :label="$t('EducationLevel')"
                                        :rules="rules.selectedLevelId"
                                        :items="levels"
                                        item-text="label"
                                        item-value="id"
                                        :disabled="levels.length === 0"
                                        v-model="selectedLevelId"
                                        :menu-props="{ offsetY: true }"
                                    ></v-select>
                                </v-col>
                            </v-row>                            
                            <v-textarea
                                rounded
                                outlined
                                required
                                no-resize
                                height="190"
                                counter="500"
                                maxlength="500"
                                v-model="message"
                                :rules="rules.message"
                                :label="$t('WriteMessage') + ' (' + tutorName + ')'"
                            ></v-textarea>
                            <v-card flat color="grey lighten-4" class="custom-radius mb-8">
                                <v-card-subtitle class="font-weight-medium" v-text="$t('FileSupportMessageForBooking')"></v-card-subtitle>
                                <div class="pa-4">
                                    <div class="d-flex">
                                        <v-file-input 
                                            dense
                                            rounded
                                            outlined
                                            multiple
                                            hide-details
                                            label="Upload documents"
                                            class="mr-4"
                                            prepend-icon=""
                                            prepend-inner-icon="mdi-attachment"
                                            accept=".doc, .docx, .numbers, .odt, .pages, .pdf, .rtf, .txt, .wpd, .wps, .xls, .xlsx"
                                            ref="fileInput"
                                            :error="hasFileUpladReachTheLimit"
                                            @change="attachedFilesEventHandler"
                                            @click:clear="clearAttachedFilesEventHandler"
                                        ></v-file-input>
                                    </div>
                                    <div class="body-2 pt-2 text-center grey--text text--darken-2 font-weight-medium" v-text="attachedFilesSizeDisplay"></div>
                                </div>
                                <template v-if="attachedFiles.length > 0">
                                    <div class="pb-2">
                                        <v-divider></v-divider>
                                        <template v-for="(file, index) in attachedFiles">
                                            <div class="d-flex grey--text text--darken-2 align-center justify-space-between py-1 px-4" :key="`a${index}`">
                                                <div class="subtitle-2" v-text="file.file.fileName"></div>
                                                <div class="d-flex align-center">
                                                    <div class="font-weight-medium mr-2">
                                                        {{ (file.file.size / (1024*1024)).toFixed(2) }} (MB)
                                                    </div>
                                                    <v-btn icon filled @click="removeAttachedFile(index, file.file.size)">
                                                        <v-icon>mdi-close-circle-outline</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                            <v-divider v-if="index + 1 < attachedFiles.length" :key="`b${index}`"></v-divider>
                                        </template>
                                    </div>
                                </template>
                            </v-card>
                            <div class="text-center">
                                <v-btn
                                    rounded
                                    depressed
                                    color="primary"
                                    :loading="startBookingRequest"
                                    :disabled="timeslot.isBooked || !isBookingFormValid"
                                    @click="bookSession"
                                >{{ $t('BookCalendarSession') }}</v-btn>
                            </div>
                        </template>
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </v-container>
</template>

<script>
    import moment from 'moment'
    import TutorHero from '@/components/tutorHero'
    import { mapGetters } from 'vuex'

    export default {
        name: 'bookSession',
        data: () => ({
            tutorId: 0,
            slotId: 0,
            tutor: {},
            timeslot: {},
            price: null,
            levels: [],
            langauges: [],
            attachedFiles: [],
            fileUploadUsedBytes: 0,
            fileUploadMaxAvailableBytes: 5242880,
            selectedLevelId: 0,
            selectedSubjectId: 0,
            message: '',
            rules: {
                subjectId: [val => val > 0],
                selectedLevelId: [val => val > 0],
                message: [val => (val || '').length > 0],
            },
            bookedSuccessfull: false,
            timeAlreadyBooked: false,
            startBookingRequest: false,
            snackbar: {
                show: true,
                message: ''
            },
            loading: true
        }),
        components: {
            TutorHero
        },
        computed: {
            ...mapGetters(['user', 'baseImageUrl', 'preferedLanguage']),
            tutorName() {
                return this.tutor.userData.firstName + ' ' + this.tutor.userData.lastName
            },
            profilePicture() {
                return this.baseImageUrl + this.tutor.userData.photo
            },
            isBookingFormValid() {
                if(this.selectedSubjectId > 0 && this.selectedLevelId > 0 && this.message) {
                    return true
                }
                return false
            },
            hasFileUpladReachTheLimit() {
                return this.fileUploadUsedBytes >= this.fileUploadMaxAvailableBytes
            },
            attachedFilesSizeDisplay() {
                let usedSpace = this.fileUploadUsedBytes / (1024*1024)
                let availableSpace = this.fileUploadMaxAvailableBytes / (1024*1024)
                return `${usedSpace.toFixed(2)}/${availableSpace.toFixed(2)} (MB)`
            }
        },
        mounted() {
            this.fetchBookingInfo()
        },
        methods: {
            async fetchBookingInfo() {
                this.tutorId = parseInt(this.$route.params.tutorId)
                this.slotId = parseInt(this.$route.params.slotId)

                await this.axios.get('/Booking/GetBookingInfo', {
                    params: {
                        tutorId: this.tutorId,
                        timeslotId: this.slotId
                    }
                })
                .then(response => {
                    this.tutor = response.data.tutor
                    this.timeslot = response.data.timeslot
                    this.price = response.data.price
                    this.languages = response.data.languages
                })
                .catch(error => {
                    this.snackbar.message = error.response.data
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
                this.loading = false
            },
            bookSession() {
                this.startBookingRequest = true
                const data = {
                    tutorId: this.tutorId,
                    timeslotId: this.timeslot.id,
                    teachingSubject: this.selectedSubjectId,
                    educationLevelId: this.selectedLevelId,
                    message: this.message,
                    files: this.attachedFiles
                }
                this.axios.post('/Booking/AddBooking', data)
                .then(response => {
                    //var hasKey = Object.keys(response.data).includes('timeAlreadyBooked');
                    if(response.data.hasOwnProperty('timeAlreadyBooked')){
                        this.snackbar.message = this.$t('TimeAlreadyBooked')
                        this.$EventBus.$emit('snackbar', this.snackbar)
                        this.timeAlreadyBooked = true
                        this.startBookingRequest = false
                    }else{
                        this.message = ''
                        this.selectedSubjectId = 0
                        this.selectedLevelId = 0
                        this.timeslot.isBooked = true
                        this.bookedSuccessfull = true
                        this.timeAlreadyBooked = false
                        this.startBookingRequest = false
                        this.attachedFiles = []
                    }
                })
                .catch(error => {
                    this.snackbar.message = error.response.data
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.startBookingRequest = false
                })
            },
            filterEducationLevelsBySubject() {
                this.axios.get('/Booking/GetEducationLevelByTutorAndTeachingSubject', {
                    params: {
                        tutorId: this.tutorId,
                        tutorSubject: this.selectedSubjectId,
                        language: this.preferedLanguage,
                    }
                })
                .then(response => {
                    this.levels = response.data.educationLevels
                })
                .catch(error => {
                    this.snackbar.message = error.response.data
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
            },
            dateFormat(date) {
                return moment(date).format("MM/DD/YYYY")
            },
            time12hFormat(time) {
                return moment(time).format("LT")
            },
            attachedFilesEventHandler(files) {
                files.map(file => {
                    this.fileUploadUsedBytes += file.size
                    if(this.fileUploadUsedBytes <= this.fileUploadMaxAvailableBytes) {
                        const _file = {
                            file: {
                                fileName: file.name.split('.')[0],
                                fileExtension: file.name.split('.')[1],
                                size: file.size,
                                base64String: ''
                            },
                            fileName: file.name.split('.')[0]
                        }
                        var reader = new FileReader()
                        reader.readAsDataURL(file)
                        reader.onload = () => {
                            _file.file.base64String = reader.result.replace(/^data:.+;base64,/, '')
                        }
                        this.attachedFiles.push(_file)
                    }
                })
            },
            removeAttachedFile(index, size) {
                this.attachedFiles.splice(index, 1)
                this.fileUploadUsedBytes -= size
            },
            clearAttachedFilesEventHandler() {
                this.fileUploadUsedBytes = 0
                this.attachedFiles.map(file => {
                    this.fileUploadUsedBytes += file.file.size
                })
            }
        },
        watch: {
            selectedSubjectId() {
                this.filterEducationLevelsBySubject()
            }
        }
    }
</script>

<style lang="scss" scoped></style>